import React, { useState, useEffect, useCallback, useMemo } from "react";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import Sidebar from "../../../components/layout/Sidebar";
import Select from "react-select";
import { Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";
import { IoSearch } from "react-icons/io5";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CenterTimeChange = () => {
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [croOptions, setCroOptions] = useState([]);
  const [selectedCro, setSelectedCro] = useState(null);
  const [centers, setCenters] = useState([]);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [updatedTimes, setUpdatedTimes] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const transformCroData = (data) =>
    data.map((item) => ({
      value: item.id,
      label: item.username,
    }));

  const getCroData = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getCroData`);
      if (response.data.message) {
        setCroOptions(transformCroData(response.data.message));
      } else {
        setCroOptions([]);
      }
    } catch (error) {
      console.error("Error fetching CROs:", error);
    }
  }, []);

  useEffect(() => {
    getCroData();
  }, [getCroData]);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const handleCroChange = async (selectedOption) => {
    setSelectedCro(selectedOption);
    setSelectedCenters([]);
    setUpdatedTimes({});
    setSearchTerm("");
    if (!selectedOption) {
      setCenters([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/getModelData/center?fields=id,name,bmMeetingDayOrder,bmMeetingTime&filter={"fieldManagerid":${selectedOption.value}}`
      );
      setCenters(response.data.data || []);
    } catch (error) {
      console.error("Error fetching centers for CRO:", error);
      toast.error("Failed to load centers for selected CRO");
    } finally {
      setLoading(false);
    }
  };

  const handleCenterChange = (centerId) => {
    if (selectedCenters.includes(centerId)) {
      setSelectedCenters(selectedCenters.filter((id) => id !== centerId));
      setUpdatedTimes((prev) => {
        const next = { ...prev };
        delete next[centerId];
        return next;
      });
    } else {
      setSelectedCenters([...selectedCenters, centerId]);
    }
  };

  const handleTimeChange = (centerId, newTime) => {
    setUpdatedTimes((prev) => ({
      ...prev,
      [centerId]: newTime,
    }));
  };

  const validateTimeFormat = (time) => {
    if (!time || time.trim() === "") return false;
    const regex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
    return regex.test(time);
  };

  const handleSubmit = async () => {
    if (selectedCenters.length === 0) {
      toast.warning("Please select at least one center to update.");
      return;
    }

    const updatedTimesWithDefaults = selectedCenters.reduce((acc, centerId) => {
      acc[centerId] = updatedTimes[centerId] || "";
      return acc;
    }, {});

    const invalidCenters = [];
    Object.entries(updatedTimesWithDefaults).forEach(([centerId, time]) => {
      if (!validateTimeFormat(time)) {
        invalidCenters.push(centerId);
      }
    });

    if (invalidCenters.length > 0) {
      const invalidCenterNames = invalidCenters.map((centerId) => {
        const center = centers.find((c) => c.id === parseInt(centerId));
        return center ? center.name : `ID: ${centerId}`;
      });

      toast.error(
        `Invalid time format (use hh:mm:ss) for: ${invalidCenterNames.join(", ")}`
      );
      return;
    }

    const payload = selectedCenters.map((centerId) => ({
      centerId,
      newTime: updatedTimes[centerId] || "",
    }));

    try {
      const response = await axios.post(`${BASE_URL}/api/updateCenterTimes`, {
        data: payload,
      });
      if (response.data.message) {
        toast.success("Center times updated successfully!");
        handleCroChange(selectedCro);
      } else {
        toast.error(response.data.error || "Failed to update times.");
      }
    } catch (error) {
      console.error("Error updating times:", error);
      toast.error("An error occurred while updating center times.");
    }
  };

  const filteredCenters = useMemo(() => {
    if (!searchTerm.trim()) return centers;
    const q = searchTerm.toLowerCase().trim();
    return centers.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [centers, searchTerm]);

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section inline-header">
            <h1 className="TableHeaderText m-0">Center Time Change</h1>
          </div>

          {/* Filter Section */}
          <div className="master-search-section py-3 px-4">
            <Row className="w-100 g-3 align-items-center">
              <Col xs={12} md={6}>
                <label className="form-label fw-semibold text-muted mb-1">
                  Select CRO
                </label>
                <Select
                  value={selectedCro}
                  onChange={handleCroChange}
                  options={croOptions}
                  placeholder="Select CRO..."
                  isClearable
                  className="filter-dropdown"
                />
              </Col>
            </Row>
          </div>

          {/* Center Table List */}
          <div className="master-list-section">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : selectedCro ? (
              <div className="w-100">
                {/* Search Bar */}
                <div className="d-flex justify-content-end align-items-center mb-3">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search centers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    <span className="search-icon">
                      <IoSearch />
                    </span>
                  </div>
                </div>

                <div className="master-table-wrapper table-responsive">
                  <table className="mx-auto table table-striped table-list">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>Select</th>
                        <th style={{ width: "70px" }}>No</th>
                        <th>Center Name</th>
                        <th>BM Meeting Day</th>
                        <th>BM Meeting Time</th>
                        <th style={{ width: "220px" }}>New Time (hh:mm:ss)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCenters.length > 0 ? (
                        filteredCenters.map((center, index) => {
                          const isSelected = selectedCenters.includes(center.id);
                          return (
                            <tr key={center.id}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  id={`center-${center.id}`}
                                  checked={isSelected}
                                  onChange={() => handleCenterChange(center.id)}
                                />
                              </td>
                              <td>{index + 1}</td>
                              <td className="fw-semibold text-start">
                                {center.name}
                              </td>
                              <td>{center.bmMeetingDayOrder || "-"}</td>
                              <td>{center.bmMeetingTime || "-"}</td>
                              <td>
                                {isSelected ? (
                                  <input
                                    type="text"
                                    className="form-control form-control-sm text-center"
                                    placeholder="09:30:00"
                                    value={updatedTimes[center.id] || ""}
                                    onChange={(e) =>
                                      handleTimeChange(center.id, e.target.value)
                                    }
                                    style={{ maxWidth: "160px", margin: "0 auto" }}
                                  />
                                ) : (
                                  <span className="text-muted small">
                                    Check to edit
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            No centers found for this CRO
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                Please select a CRO to view and update its centers' meeting times.
              </div>
            )}
          </div>

          {/* Save Changes Footer */}
          {selectedCenters.length > 0 && (
            <div className="master-pagination-wrapper py-3">
              <button
                type="button"
                className="btn btn-addCredential m-0"
                onClick={handleSubmit}
              >
                Save Changes ({selectedCenters.length} Selected)
              </button>
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-center" />
    </section>
  );
};

export default CenterTimeChange;
